// Pure slot → card mapping for the Worker Review drawer.
//
// A submission with `decorationSchemaVersion: 2` carries
// `tier{N}DecorationSlots` — one entry per decoration LOCATION on the garment
// (two logos plus a player name plus a number is the shape that motivated
// this). The drawer used to render one card per TIER bound to the flat
// tier{N}LogoPath / tier{N}Preflight* fields, so everything past slot 0 was
// invisible to the back office.
//
// Everything here is pure so the grouping/ordering/labelling rules are tested
// without a DOM. Mirrors the server reader in ../../../lib/submission-slots.js
// (copy-not-import, per house convention).
import {
  resolveLocation, isDeclaredStackPair,
  VALID_KINDS, VALID_VIEWS, VIEW_LABELS,
  type DecorationKind, type DecorationRegistry, type DecorationLocation,
  type MockupViewName, type PrintArea,
} from './prize-product';
import { MAX_PRIZE_TIERS } from './prize-tiers';

/** Alias kept for the existing import sites; the vocabulary lives in one place. */
export type DecorationKindName = DecorationKind;
export type SlotStatus = 'PASS' | 'FAIL' | 'ERROR';

export type DecorationSlot = {
  slotId: string;
  code: string;
  kind: DecorationKindName;
  font?: string | null;
  ink?: string | null;
  logoPath?: string | null;
  logoSha256?: string | null;
  designId?: string | number | null;
  status?: SlotStatus | null;
  dimensions?: string | null;
  messages?: string[] | null;
  errors?: string[] | null;
  warnings?: string[] | null;
  trimmedLogoPath?: string | null;
  heatmapPath?: string | null;
  mockupView?: MockupViewName | null;
  validatedTemplate?: boolean | null;
  preflightRevision?: number | null;
  preflightAt?: string | null;
  sourceLogoPath?: string | null;
  error?: string | null;
  textAdded?: string | null;
  fontUsed?: string | null;
  decoration?: {
    text?: string;
    font?: string;
    textColor?: string;
    outline?: { on?: boolean; px?: number };
  } | null;
};

// One `tiers[]` entry as the drawer reads it. T3+ live only here — there is
// no `tier3DecorationSlots` dual-write.
export type SlotBearingTier = {
  decorationSlots?: unknown;
  mockupPaths?: Record<string, string> | null;
  mockupPath?: string | null;
  preflightStatus?: string | null;
  preflightAt?: string | null;
  useSameLogo?: boolean | null;
};

// The subset of the submission doc this module reads.
export type SlotBearingSubmission = {
  decorationSchemaVersion?: number | null;
  tier1DecorationSlots?: DecorationSlot[] | null;
  tier2DecorationSlots?: DecorationSlot[] | null;
  tier1PreflightStatus?: string | null;
  tier2PreflightStatus?: string | null;
  tier1PreflightAt?: string | null;
  tier2PreflightAt?: string | null;
  tier2UseSameLogo?: boolean | string | null;
  tiers?: SlotBearingTier[] | null;
};

/** 1..MAX_PRIZE_TIERS, or null when the caller named a product that cannot exist. */
export function normalizePrizeTier(tier: unknown): number | null {
  const n = Number(tier);
  return Number.isInteger(n) && n >= 1 && n <= MAX_PRIZE_TIERS ? n : null;
}

export type SlotCard = {
  slotId: string;
  index: number;
  code: string;
  kind: DecorationKindName;
  /** Registry label, or an explicit "unknown code" label — never silently blank. */
  label: string;
  view: MockupViewName | null;
  sort: number;
  /** The registry has no entry for this code: shown, flagged, sorted last. */
  unknownLocation: boolean;
  /** The tier's product has no print area for this code. */
  missingPrintArea: boolean;
  widthInches?: number;
  heightInches?: number;
  font?: string;
  maxChars?: number;
  maxDigits?: number;
  ink?: string;
  status: SlotStatus | null;
  designId: string | number | null;
  dimensions: string | null;
  messages: string[];
  errors: string[];
  warnings: string[];
  error: string | null;
  validatedTemplate: boolean | null;
  preflightRevision: number | null;
  hasTrimmed: boolean;
  hasHeatmap: boolean;
  /** Slot 0 — the tier's primary logo, mirrored to tier{N}LogoPath. */
  isPrimary: boolean;
  /**
   * Set when this slot and one other are the registry's DECLARED name-over-
   * number stack (`stacksWith`, both halves). Never inferred from the view.
   */
  pairRole: 'name' | 'number' | null;
  /** The partner's label, for the "Stacked with …" line. */
  pairPartner: string | null;
  /** The partner's location code — the declaration this pairing came from. */
  pairPartnerCode: string | null;
  captionText?: string | null;
  captionFont?: string | null;
  captionTextColor?: string | null;
};

export type TierRollup = {
  status: 'PASS' | 'FAIL' | 'NOT_RUN';
  total: number;
  passed: number;
  logoCount: number;
  textCount: number;
  failedSlotIds: string[];
  /** The submission's flat `tier{N}PreflightStatus`, as passed in. */
  flatStatus: string | null;
  /**
   * Set when the cards and the flat tier status disagree — the state a
   * re-preflight resolves. Null when they agree.
   */
  blockedReason: string | null;
};

function dualWriteSlots(sub: SlotBearingSubmission, t: number): DecorationSlot[] | null {
  const dual = (sub as Record<string, unknown>)[`tier${t}DecorationSlots`];
  if (Array.isArray(dual) && dual.length > 0) return dual as DecorationSlot[];
  return null;
}

function tiersArraySlots(sub: SlotBearingSubmission, t: number): DecorationSlot[] | null {
  if (!Array.isArray(sub.tiers) || sub.tiers.length === 0) return null;
  const fromTiers = sub.tiers[t - 1] && sub.tiers[t - 1]!.decorationSlots;
  if (Array.isArray(fromTiers) && fromTiers.length > 0) return fromTiers as DecorationSlot[];
  return null;
}

/**
 * The tier's slots, or null for a LEGACY submission / a product with no slots.
 *
 * Precedence matches the server reader: slots only count when
 * `decorationSchemaVersion >= 2`. Dual-write `tier{1|2}DecorationSlots` wins
 * when present; T3+ live on `tiers[t-1].decorationSlots` (no invented
 * `tier3DecorationSlots` field). A product with no slots returns null so the
 * drawer does not invent a Run preflight control.
 */
export function readTierSlots(
  sub: SlotBearingSubmission | null | undefined,
  tier: number,
): DecorationSlot[] | null {
  if (!sub) return null;
  const t = normalizePrizeTier(tier);
  if (!t) return null;
  if (!(Number(sub.decorationSchemaVersion) >= 2)) return null;
  return dualWriteSlots(sub, t) || tiersArraySlots(sub, t);
}

/**
 * Does this product ride T1's primary mark?
 *
 * T3+ live on `tiers[n-1].useSameLogo` only. T2 also accepts the historical
 * `tier2UseSameLogo` flat. Empty logoPath alone does not invent a share.
 */
export function prizeUsesSameLogo(
  sub: SlotBearingSubmission | null | undefined,
  tier: number,
): boolean {
  const t = normalizePrizeTier(tier);
  if (!sub || !t || t <= 1) return false;
  const entry = tiersArrayEntry(sub, t);
  if (entry && entry.useSameLogo === true) return true;
  if (t <= 2) {
    const flat = (sub as Record<string, unknown>)[`tier${t}UseSameLogo`];
    return flat === true || flat === 'true';
  }
  return false;
}

/** Worker Review banner. Placement count is T1's recorded slots. */
export function sameLogoBannerText(
  sub: SlotBearingSubmission | null | undefined,
  tier: number,
): string {
  void tier;
  const slots = readTierSlots(sub, 1);
  const n = slots && slots.length ? slots.length : 0;
  if (n <= 0) return 'Mirrors T1 primary logo';
  const words: Record<number, string> = {
    1: 'one', 2: 'two', 3: 'three', 4: 'four', 5: 'five', 6: 'six', 7: 'seven', 8: 'eight',
  };
  const count = words[n] || String(n);
  if (n === 1) return 'Mirrors T1 primary logo · one placement';
  return `Mirrors T1 primary logo · ${count} placements`;
}

/**
 * Slots the Worker Review card should list. T3+ same-logo inherits T1
 * locations even when intake left `decorationSlots` empty. T2 with its own
 * slots keeps them.
 */
export function reviewSlotsForTier(
  sub: SlotBearingSubmission | null | undefined,
  tier: number,
): { slots: DecorationSlot[] | null; mirroredFromTier: number | null } {
  const t = normalizePrizeTier(tier);
  const own = t ? readTierSlots(sub, t) : null;
  if (!t || !prizeUsesSameLogo(sub, t)) return { slots: own, mirroredFromTier: null };
  const inherit = t > 2 || !own;
  if (!inherit) return { slots: own, mirroredFromTier: null };
  const t1 = readTierSlots(sub, 1);
  if (t1 && t1.length) return { slots: t1, mirroredFromTier: 1 };
  return { slots: own, mirroredFromTier: null };
}

/** True when the drawer should show a per-product Run preflight control. */
export function showsRunPreflight(
  sub: SlotBearingSubmission | null | undefined,
  tier: number,
): boolean {
  return readTierSlots(sub, tier) != null;
}

function tiersArrayEntry(sub: SlotBearingSubmission | Record<string, unknown> | null | undefined, t: number): SlotBearingTier | null {
  if (!sub || !Array.isArray((sub as SlotBearingSubmission).tiers)) return null;
  const entry = (sub as SlotBearingSubmission).tiers![t - 1];
  return entry && typeof entry === 'object' ? entry : null;
}

/** Flat preflight rollup: dual-write T1/T2, else `tiers[t-1].preflightStatus`. */
export function flatPreflightStatus(
  sub: SlotBearingSubmission | Record<string, unknown> | null | undefined,
  tier: number,
): string | null {
  if (!sub) return null;
  const t = normalizePrizeTier(tier);
  if (!t) return null;
  const dual = (sub as Record<string, unknown>)[`tier${t}PreflightStatus`];
  if (typeof dual === 'string' && dual) return dual;
  const fromTiers = tiersArrayEntry(sub, t)?.preflightStatus;
  return typeof fromTiers === 'string' && fromTiers ? fromTiers : null;
}

export function flatPreflightAt(
  sub: SlotBearingSubmission | Record<string, unknown> | null | undefined,
  tier: number,
): string | null {
  if (!sub) return null;
  const t = normalizePrizeTier(tier);
  if (!t) return null;
  const dual = (sub as Record<string, unknown>)[`tier${t}PreflightAt`];
  if (typeof dual === 'string' && dual) return dual;
  const fromTiers = tiersArrayEntry(sub, t)?.preflightAt;
  return typeof fromTiers === 'string' && fromTiers ? fromTiers : null;
}

/** `resolution_dpi` -> `Resolution DPI`. Acronyms the engine uses stay upper. */
const CHECK_ACRONYMS = new Set(['dpi', 'png', 'svg', 'cmyk', 'rgb', 'id']);
function humanizeCheck(check: string): string {
  return check
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((w) => (CHECK_ACRONYMS.has(w.toLowerCase()) ? w.toUpperCase() : w.charAt(0).toUpperCase() + w.slice(1)))
    .join(' ');
}

/**
 * The preflight engine returns objects like
 * `{check, message, severity, status}`. Stringifying them put raw JSON in front
 * of a back-office worker. Show the sentence the engine already wrote, labelled
 * by its check. Anything genuinely unreadable still falls through to JSON so we
 * never silently drop a finding.
 */
function asArray(v: unknown): string[] {
  if (!Array.isArray(v)) return [];
  return v
    .map((x) => {
      if (typeof x === 'string') return x;
      if (x && typeof x === 'object') {
        const o = x as { check?: unknown; message?: unknown };
        const msg = typeof o.message === 'string' ? o.message.trim() : '';
        if (msg) {
          const check = typeof o.check === 'string' && o.check.trim() ? humanizeCheck(o.check.trim()) : '';
          return check ? `${check} — ${msg}` : msg;
        }
      }
      return JSON.stringify(x);
    })
    .filter(Boolean);
}

function locationFor(reg: DecorationRegistry | undefined, code: string): DecorationLocation | null {
  if (!reg) return null;
  try {
    return resolveLocation(reg, code);
  } catch {
    return null;
  }
}

/**
 * One card per slot, in registry sort order.
 *
 * Unknown location codes are NOT dropped — a dropped slot is a decoration the
 * customer paid for that nobody reviews. They sort last and carry
 * `unknownLocation: true` so the card can say so out loud.
 */
export function buildSlotCards(
  slots: DecorationSlot[] | null | undefined,
  reg: DecorationRegistry | undefined,
  printAreas: Record<string, PrintArea> | undefined,
): SlotCard[] {
  if (!Array.isArray(slots)) return [];
  const areas = printAreas || {};

  const cards: SlotCard[] = slots.map((slot, index) => {
    const code = String(slot?.code ?? '');
    const loc = locationFor(reg, code);
    const area = areas[code];
    const kind = VALID_KINDS.includes(slot?.kind as DecorationKind) ? (slot.kind as DecorationKind) : 'logo';
    return {
      slotId: String(slot?.slotId ?? `t?s${index}`),
      index,
      code,
      kind,
      label: loc ? loc.label : `Unrecognized location “${code || '(blank)'}”`,
      view: loc ? loc.view : (slot?.mockupView ?? null),
      sort: loc ? loc.sort : Number.MAX_SAFE_INTEGER,
      unknownLocation: !loc,
      missingPrintArea: !area,
      widthInches: area?.widthInches,
      heightInches: area?.heightInches,
      font: (typeof slot?.font === 'string' && slot.font) || area?.font,
      maxChars: area?.maxChars,
      maxDigits: area?.maxDigits,
      ink: (typeof slot?.ink === 'string' && slot.ink) || area?.ink,
      status: (slot?.status as SlotStatus) ?? null,
      designId: slot?.designId ?? null,
      dimensions: slot?.dimensions ?? null,
      messages: asArray(slot?.messages),
      errors: asArray(slot?.errors),
      warnings: asArray(slot?.warnings),
      error: slot?.error ?? null,
      validatedTemplate: slot?.validatedTemplate ?? null,
      preflightRevision: Number.isFinite(slot?.preflightRevision as number) ? (slot!.preflightRevision as number) : null,
      hasTrimmed: !!slot?.trimmedLogoPath,
      hasHeatmap: !!slot?.heatmapPath,
      isPrimary: index === 0,
      captionText: kind === 'logo'
        ? ((typeof slot?.decoration?.text === 'string' && slot.decoration.text.trim())
          || (typeof slot?.textAdded === 'string' && slot.textAdded.trim())
          || null)
        : null,
      captionFont: kind === 'logo'
        ? ((typeof slot?.decoration?.font === 'string' && slot.decoration.font)
          || (typeof slot?.fontUsed === 'string' && slot.fontUsed)
          || null)
        : null,
      captionTextColor: kind === 'logo'
        ? ((typeof slot?.decoration?.textColor === 'string' && slot.decoration.textColor) || null)
        : null,
      pairRole: null,
      pairPartner: null,
      pairPartnerCode: null,
    };
  });

  markStackedPairs(cards, reg);
  // The PRIMARY slot always leads. Registry sort is garment anatomy (left chest
  // before full chest), which buried the tier's main logo under a secondary one
  // — a reviewer scanning the drawer saw the wrong artwork first. Everything
  // after the primary keeps registry sort, then declaration order (two
  // locations may share a sort key).
  return cards.slice().sort((a, b) => {
    if (a.isPrimary !== b.isPrimary) return a.isPrimary ? -1 : 1;
    return a.sort - b.sort || a.index - b.index;
  });
}

/**
 * Mark the tier's DECLARED name-over-number stack (e.g. "SMITH" over "12" on the
 * back). The worker has to know the two cards are one print or they read as two
 * separate decorations in two separate places.
 *
 * WHICH TWO LOCATIONS THAT IS COMES FROM THE REGISTRY (Codex F9, 2026-08-11).
 * This used to mark EVERY name+number sharing a garment view, which is a
 * DIFFERENT question and answers it wrong in two ways: an undeclared pair got
 * labelled "Stacked with…" (a garment that gains a sleeve number would grow a
 * stack nobody declared), and a real declared pair fanned its partner line out
 * across every other number on that view. The admin v2 form and requestedit both
 * read `stacksWith`; this console was the odd one out, so a worker could be told
 * two prints are one when the customer was never offered them that way.
 *
 * Both halves must name each other — requestedit/lib/slot-edit.js
 * stackedNameNumberPreset: "a half-declared pair is NOT a pair". The same-view
 * check stays as a SECONDARY guard: it no longer decides anything on its own,
 * but a declaration whose two locations render on different sides is not the
 * stack this card's copy describes ("both print in the … area").
 */
export function markStackedPairs(cards: SlotCard[], reg: DecorationRegistry | undefined): void {
  if (!reg?.locations) return;
  for (const a of cards) {
    if (a.kind !== 'name' && a.kind !== 'number') continue;
    const partner = cards.find((b) => (
      b !== a
      && (b.kind === 'name' || b.kind === 'number')
      && b.kind !== a.kind
      && isDeclaredStackPair(reg, a.code, b.code)
      && a.view === b.view
    ));
    if (!partner) continue;
    a.pairRole = a.kind;
    a.pairPartner = partner.label;
    a.pairPartnerCode = partner.code;
  }
}

/**
 * The tier headline. Mirrors stahlspreflight/lib/slot-preflight.js tierRollup:
 * PASS only when every slot passed AND every text slot's template validated.
 * A tier where nothing has run yet is NOT_RUN, not FAIL — the difference is
 * what tells the worker whether to press Run Preflight or fix artwork.
 *
 * `flatTierStatus` is the submission's `tier{N}PreflightStatus`, and it is a
 * REQUIRED argument, not an optional refinement. approveproof gates on BOTH the
 * slots and that flat field (approveproof/lib/decoration-approval.js R1: every
 * reset writer clears it, so a rollup that is not PASS means the slot results
 * describe artwork that is no longer current). A drawer that rendered PASS off
 * the cards alone would promise an approval the server refuses, so the two must
 * agree here or the tier is not PASS and the disagreement is named out loud.
 */
export function tierRollupFromCards(
  cards: SlotCard[],
  flatTierStatus: string | null | undefined,
): TierRollup {
  const failedSlotIds: string[] = [];
  let passed = 0;
  let logoCount = 0;
  let textCount = 0;
  let anyRun = false;
  for (const c of cards) {
    if (c.kind === 'logo') logoCount += 1;
    else textCount += 1;
    if (c.status) anyRun = true;
    const ok = c.status === 'PASS' && (c.kind === 'logo' || c.validatedTemplate === true);
    if (ok) passed += 1;
    else failedSlotIds.push(c.slotId);
  }

  const flatStatus = flatTierStatus == null ? null : String(flatTierStatus);
  const flatPass = flatStatus === 'PASS';
  const cardsPass = anyRun && failedSlotIds.length === 0;
  const shown = flatStatus ? `“${flatStatus}”` : 'unset';

  let blockedReason: string | null = null;
  if (cardsPass && !flatPass) {
    blockedReason = `All ${cards.length} locations pass, but this tier's preflight status is ${shown}. `
      + 'Approval reads that field and will refuse — a logo replacement, text edit or product change clears it. '
      + 'Re-run preflight for this tier.';
  } else if (anyRun && !cardsPass && flatPass) {
    blockedReason = `This tier's preflight status says PASS, but ${failedSlotIds.join(', ')} `
      + `${failedSlotIds.length === 1 ? 'is' : 'are'} not passing. The rollup is stale — re-run preflight for this tier.`;
  } else if (!anyRun && flatPass) {
    blockedReason = "This tier's preflight status says PASS but no location has been preflighted. "
      + 'The rollup is stale — re-run preflight for this tier.';
  }

  return {
    status: !anyRun ? 'NOT_RUN' : cardsPass && flatPass ? 'PASS' : 'FAIL',
    total: cards.length,
    passed,
    logoCount,
    textCount,
    failedSlotIds,
    flatStatus,
    blockedReason,
  };
}

/** "Player last name — printed at order time" and friends. */
export function textSlotDescription(card: SlotCard): string {
  const what = card.kind === 'number' ? 'Player number'
    : card.kind === 'name-number' ? 'Player name and number'
    : 'Player last name';
  return `${what} — printed at order time`;
}

/** `3.5" × 3"`, or null when the product declares no size for this location. */
export function printSizeLabel(card: SlotCard): string | null {
  const w = card.widthInches;
  const h = card.heightInches;
  if (!Number.isFinite(w) || !Number.isFinite(h)) return null;
  const fmt = (n: number) => String(Number(n.toFixed(2)));
  return `${fmt(w as number)}" × ${fmt(h as number)}"`;
}

/** The typography line for a name/number card: font, cap, ink. */
export function textSpecLabel(card: SlotCard): string[] {
  const bits: string[] = [];
  if (card.font) bits.push(card.font);
  if (Number.isFinite(card.maxChars)) bits.push(`max ${card.maxChars} chars`);
  if (Number.isFinite(card.maxDigits)) bits.push(`max ${card.maxDigits} digits`);
  if (card.ink) bits.push(`ink ${card.ink}`);
  return bits;
}

// --- server-resolved image URLs. The browser never names a GCS path: it names
// the slot, and routes/slots.js reads the path out of Firestore.
export function slotImageUrl(
  submissionId: string,
  tier: number,
  index: number,
  kind: 'logo' | 'trimmed' | 'heatmap' = 'logo',
): string {
  return `/api/submissions/${encodeURIComponent(submissionId)}/slots/${tier}/${index}/image?kind=${kind}`;
}

export function mockupImageUrl(submissionId: string, tier: number, view: MockupViewName): string {
  return `/api/submissions/${encodeURIComponent(submissionId)}/mockups/${tier}/${view}`;
}

// The view order and the view labels both come from prize-product.ts. This file
// used to keep its own copy of each, and the label copy had already drifted
// ("Left sleeve" here vs "Left Sleeve" in the zone editor) — the same garment
// side named two ways in one app.
export const viewLabel = (v: MockupViewName | null | undefined): string =>
  (v && VIEW_LABELS[v]) || '—';

/**
 * Which proof views this tier actually has. Dual-write `tier{N}MockupPaths`
 * (T1/T2) wins; T3+ live on `tiers[t-1].mockupPaths`. The legacy single
 * `tier{N}MockupPath` / `tiers[].mockupPath` counts as 'front' only.
 */
export function proofViews(
  sub: Record<string, unknown> | null | undefined,
  tier: number,
): MockupViewName[] {
  if (!sub) return [];
  const t = normalizePrizeTier(tier);
  if (!t) return [];
  const dualPaths = sub[`tier${t}MockupPaths`] as Record<string, string> | undefined | null;
  const entry = tiersArrayEntry(sub, t);
  const paths = (dualPaths && typeof dualPaths === 'object' ? dualPaths : null)
    || (entry && entry.mockupPaths && typeof entry.mockupPaths === 'object' ? entry.mockupPaths : null);
  const legacyFront = sub[`tier${t}MockupPath`] || entry?.mockupPath;
  const out: MockupViewName[] = [];
  for (const v of VALID_VIEWS) {
    if (paths && typeof paths === 'object' && paths[v]) out.push(v);
    else if (v === 'front' && legacyFront) out.push(v);
  }
  return out;
}
